import { AnnouncementFrequencies, AnnouncementTemplateDto, CommunicationConfigDto, UpdateAnnouncementDto } from '@inosio/payments-sdk';
import { isBefore, parseISO } from 'date-fns';

export interface AnnouncementSectionProps {
  setPendingAnnouncement: (announcement: Partial<UpdateAnnouncementDto> | null) => void;
}

export abstract class BaseAnnouncementItemValidator<T extends { TemplateUuid: string }> {
  constructor(protected readonly communicationConfigs: CommunicationConfigDto, protected readonly announcementResponseDto: AnnouncementTemplateDto) {}

  /**
   * Validation function to determine if all required fields are correctly populated.   *
   * @param entityObject
   * @returns true if all good. false otherwise.
   */
  protected abstract IsEntityDetailsComplete(entityObject: T): boolean;

  public IsEntireObjectComplete(entityObject: T) {
    return this.IsEntityDetailsComplete(entityObject);
  }
}

export class AnnouncementDetailValidator extends BaseAnnouncementItemValidator<AnnouncementTemplateDto> {
  constructor(protected announcementConfig: CommunicationConfigDto, protected readonly announcementResponseDto: AnnouncementTemplateDto) {
    super(announcementConfig, announcementResponseDto);
  }

  protected IsEntityDetailsComplete(announcement: AnnouncementTemplateDto): boolean {
    const { Description, Subject, SenderIdentity } = announcement;
    let isAnnouncementDetailComplete = true;

    if (!Description || !Subject || !SenderIdentity) {
      isAnnouncementDetailComplete = false;
    }

    return isAnnouncementDetailComplete;
  }
}

export class AnnouncementBodyValidator extends BaseAnnouncementItemValidator<AnnouncementTemplateDto> {
  constructor(protected announcementConfig: CommunicationConfigDto, protected readonly announcementResponseDto: AnnouncementTemplateDto) {
    super(announcementConfig, announcementResponseDto);
  }

  protected IsEntityDetailsComplete(announcement: AnnouncementTemplateDto): boolean {
    const { Body } = announcement;
    let isAnnouncementBodyComplete = true;

    if (!Body) {
      isAnnouncementBodyComplete = false;
    }

    return isAnnouncementBodyComplete;
  }
}

export class AnnouncementSendToValidator extends BaseAnnouncementItemValidator<AnnouncementTemplateDto> {
  constructor(protected announcementConfig: CommunicationConfigDto, protected readonly announcementResponseDto: AnnouncementTemplateDto) {
    super(announcementConfig, announcementResponseDto);
  }

  protected IsEntityDetailsComplete(announcement: AnnouncementTemplateDto): boolean {
    const { RecipientOptions } = announcement;
    let isAnnouncementSendToComplete = true;

    if (!RecipientOptions || !RecipientOptions.length) {
      isAnnouncementSendToComplete = false;
    }

    return isAnnouncementSendToComplete;
  }
}

export class AnnouncementScheduleValidator extends BaseAnnouncementItemValidator<AnnouncementTemplateDto> {
  constructor(protected announcementConfig: CommunicationConfigDto, protected readonly announcementResponseDto: AnnouncementTemplateDto) {
    super(announcementConfig, announcementResponseDto);
  }

  protected IsEntityDetailsComplete(announcement: AnnouncementTemplateDto): boolean {
    const { Frequency, FirstSendDatetime } = announcement;

    if (!Frequency || !FirstSendDatetime) {
      return false;
    }

    if(FirstSendDatetime && Frequency !== AnnouncementFrequencies.OneTime) {
      return true
    }
    const convertedFirstSendDateTime = parseISO(FirstSendDatetime);
    const rightNow = new Date();

    if (!convertedFirstSendDateTime || isBefore(convertedFirstSendDateTime, rightNow)) {
      return false;
    }

    // Everything is valid
    return true;
  }
}

export class AnnouncementReviewValidator extends BaseAnnouncementItemValidator<AnnouncementTemplateDto> {
  private validators: BaseAnnouncementItemValidator<AnnouncementTemplateDto>[];

  constructor(announcementConfig: CommunicationConfigDto, announcementResponseDto: AnnouncementTemplateDto, ...validators: BaseAnnouncementItemValidator<AnnouncementTemplateDto>[]) {
    super(announcementConfig, announcementResponseDto);
    this.validators = validators;
  }

  /**
   * Validates the entity using all the provided validators.
   * @param entityObject The entity to be validated.
   * @returns true if all validators pass. false otherwise.
   */
  protected IsEntityDetailsComplete(entityObject: AnnouncementTemplateDto): boolean {
    // Iterate through each validator and check if all of them are valid
    return this.validators.every((validator) => validator.IsEntireObjectComplete(entityObject));
  }
}
